/**
 * AccountCreditsPage — self-serve credits dashboard at /account/credits.
 *
 * Shows:
 *   • Current balance + lifetime topup/spend
 *   • Bundle catalogue with Stripe checkout buttons
 *   • Recent ledger entries (last 50)
 *
 * Auth is enforced server-side by /api/credits/* (require_customer dep).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Coins, Sparkles, CheckCircle2, AlertTriangle, Loader2,
  ArrowRight, TrendingUp, TrendingDown,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import "@/pages/ShowcasePage.css";

const TIER_BADGE = { starter: "Start", growth: "Growth", agency: "Agency", reseller: "Reseller" };

export default function AccountCreditsPage() {
  const [balance, setBalance] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [topupLoading, setTopupLoading] = useState("");
  const [error, setError] = useState("");
  const [requiresLogin, setRequiresLogin] = useState(false);

  useEffect(() => {
    document.title = "Credits · HustlePlaza";
    let cancelled = false;
    (async () => {
      try {
        const sessionId=new URLSearchParams(window.location.search).get("session_id");
        if(sessionId&&/^cs_[A-Za-z0-9_]+$/.test(sessionId))await api.get("/credits/topup/"+encodeURIComponent(sessionId)+"/status");
        const [b, bun, l] = await Promise.all([
          api.get("/credits/balance"),
          api.get("/credits/bundles"),
          api.get("/credits/ledger"),
        ]);
        if (cancelled) return;
        setBalance(b.data);
        setBundles(bun.data.items || []);
        setLedger(l.data.items || []);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401) { setRequiresLogin(true); }
        else setError(formatApiError(e?.response?.data?.detail) || "Could not load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleTopup = async (slug) => {
    setTopupLoading(slug); setError("");
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const { data } = await api.post("/credits/topup", {
        bundle: slug, origin_url: origin,
      });
      if (data?.url) window.location.href = data.url;
      else setError("Could not start checkout.");
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Top-up failed");
    } finally {
      setTopupLoading("");
    }
  };

  if (requiresLogin) {
    return (
      <div className="sc-root" data-testid="credits-page">
        <div className="sc-shell">
          <header className="sc-topbar">
            <Link to="/" className="sc-brand"><span className="sc-brand-dot" />HustlePlaza Credits</Link>
          </header>
          <section className="sc-section" style={{ textAlign: "center", padding: "100px 20px" }}>
            <AlertTriangle size={48} style={{ color: "var(--sc-accent)" }} />
            <h1 className="sc-h2" style={{ marginTop: 16 }} data-testid="credits-login-required">Sign in to view credits</h1>
            <p style={{ color: "var(--sc-text-dim)", marginBottom: 24 }}>
              You need an account to track credit balance and top up.
            </p>
            <Link to="/customer/login?next=/account/credits" className="sc-cta-primary" data-testid="credits-login-link">
              <Sparkles size={16} /> Sign in
            </Link>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="sc-root" data-testid="credits-page">
      <div className="sc-shell">
        <header className="sc-topbar">
          <Link to="/" className="sc-brand"><span className="sc-brand-dot" />HustlePlaza Credits</Link>
          <Link to="/showcase" className="sc-topbar-cta" data-testid="credits-platform-link">
            <Sparkles size={16} /> Platform · $9,997
          </Link>
        </header>

        {/* Balance hero */}
        <section className="sc-section" style={{ paddingTop: 60 }} data-testid="credits-hero">
          <div className="sc-pricing" style={{ gridTemplateColumns: "1.3fr 0.7fr" }}>
            <div>
              <span className="sc-eyebrow"><Coins size={12} /> Your wallet</span>
              {loading ? (
                <p className="sc-price-amount"><Loader2 className="sc-spin" /></p>
              ) : (
                <p className="sc-price-amount" data-testid="credits-balance">
                  {balance?.balance ?? 0}
                  <span className="sc-price-cents"> credits</span>
                </p>
              )}
              <p className="sc-price-tagline">
                Spend across HustleVids (video generation) and HustleHydro (flood reports).
                1 credit = 1 standard video or 1 basic flood report.
              </p>
              <ul className="sc-price-list">
                <li><TrendingUp size={16} /> Lifetime top-up: <strong style={{ marginLeft: 6 }}>{balance?.lifetime_topup ?? 0}</strong></li>
                <li><TrendingDown size={16} /> Lifetime spent: <strong style={{ marginLeft: 6 }}>{balance?.lifetime_spend ?? 0}</strong></li>
              </ul>
            </div>
            <div>
              <h3 className="sc-h3" style={{ fontSize: 22 }}>How credits work</h3>
              <ul style={{ listStyle: "none", padding: 0,
                              color: "var(--sc-text-dim)", fontSize: 14,
                              lineHeight: 1.8 }}>
                <li>🎬 Standard video — <strong>1 credit</strong></li>
                <li>🎬 Premium video (HeyGen) — <strong>4 credits</strong></li>
                <li>🎬 Cinematic Sora-2 — <strong>8 credits</strong></li>
                <li>🌊 Hydro basic — <strong>1 credit</strong></li>
                <li>🌊 Hydro full + PDF — <strong>3 credits</strong></li>
              </ul>
            </div>
          </div>
          {error ? <p style={{ color: "#ff8b73", textAlign: "center", marginTop: 18 }} data-testid="credits-error">{error}</p> : null}
        </section>

        {/* Bundles */}
        <section className="sc-section" data-testid="credits-bundles-section">
          <header className="sc-section-header">
            <h2 className="sc-h2">Top up your credits</h2>
            <p className="sc-section-desc">
              One-off purchase. No subscription. Bundles save big on Stripe fees.
            </p>
          </header>
          <div className="sc-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
            {bundles.map((b) => (
              <article key={b.slug}
                          className="sc-include-card"
                          style={{ position: "relative" }}
                          data-testid={`credits-bundle-${b.slug}`}>
                <span style={{
                  position: "absolute", top: 14, right: 14,
                  fontSize: 11, fontWeight: 600, letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: b.slug === "growth" ? "var(--sc-accent)" : "var(--sc-text-mute)",
                  background: b.slug === "growth" ? "rgba(255,178,77,0.12)" : "rgba(255,255,255,0.05)",
                  border: "1px solid",
                  borderColor: b.slug === "growth" ? "rgba(255,178,77,0.3)" : "rgba(255,255,255,0.08)",
                  padding: "3px 10px", borderRadius: 999,
                }}>{TIER_BADGE[b.slug] || b.slug}</span>
                <Coins size={24} />
                <h3 className="sc-h3" style={{ fontSize: 26 }}>{b.credits}</h3>
                <p style={{ color: "var(--sc-text-mute)", fontSize: 13,
                                margin: "0 0 12px" }}>credits</p>
                <p style={{ fontSize: 22, fontWeight: 700, color: "var(--sc-accent)" }}>
                  ${b.amount.toFixed(2)}
                  <span style={{ fontSize: 11, color: "var(--sc-text-mute)",
                                    marginLeft: 6 }}>{b.currency.toUpperCase()}</span>
                </p>
                <p style={{ fontSize: 12, color: "var(--sc-text-mute)",
                                margin: "0 0 14px" }}>
                  ≈ ${(b.amount / b.credits).toFixed(2)} per credit
                </p>
                <button type="button" className="sc-cta-primary"
                           style={{ width: "100%", justifyContent: "center" }}
                           disabled={topupLoading === b.slug}
                           onClick={() => handleTopup(b.slug)}
                           data-testid={`credits-bundle-buy-${b.slug}`}>
                  {topupLoading === b.slug ? <Loader2 size={16} className="sc-spin" /> : <ArrowRight size={16} />}
                  Buy
                </button>
              </article>
            ))}
          </div>
        </section>

        {/* Ledger */}
        <section className="sc-section" data-testid="credits-ledger-section">
          <header className="sc-section-header">
            <h2 className="sc-h2">Recent activity</h2>
          </header>
          {ledger.length === 0 ? (
            <p style={{ color: "var(--sc-text-mute)", textAlign: "center", padding: 40 }} data-testid="credits-ledger-empty">
              No activity yet. Buy a bundle to get started.
            </p>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {ledger.map((row, i) => (
                <article key={row.id || i}
                            style={{
                              padding: "14px 18px",
                              background: "var(--sc-card)",
                              border: "1px solid var(--sc-line)",
                              borderRadius: 12,
                              display: "grid",
                              gridTemplateColumns: "auto 1fr auto",
                              gap: 16, alignItems: "center",
                            }}
                            data-testid={`credits-ledger-row-${i}`}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 12,
                    background: row.delta > 0 ? "rgba(108,220,255,0.1)" : "rgba(255,165,60,0.1)",
                    display: "grid", placeItems: "center",
                    color: row.delta > 0 ? "var(--sc-accent-3)" : "var(--sc-accent)",
                  }}>
                    {row.delta > 0 ? <TrendingUp size={18} /> : <TrendingDown size={18} />}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{row.reason}</div>
                    <div style={{ color: "var(--sc-text-mute)", fontSize: 12 }}>
                      {new Date(row.created_at).toLocaleString("en-AU")} · {row.kind}
                    </div>
                  </div>
                  <div style={{
                    fontWeight: 700, fontSize: 16,
                    color: row.delta > 0 ? "var(--sc-accent-3)" : "var(--sc-accent)",
                  }}>
                    {row.delta > 0 ? "+" : ""}{row.delta} · {row.balance_after}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <footer className="sc-footer">
          <div>HustlePlaza Credits · <Link to="/">Home</Link> · <Link to="/showcase">Get the platform</Link></div>
        </footer>
      </div>

      <style>{`
        .sc-spin { animation: scSpin 1s linear infinite; }
        @keyframes scSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
